import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useSkills } from '@hooks/useSkills';
import { ArrowRight, CheckCircle, Bell, Lock } from 'lucide-react';
import { supabase } from '@services/api.js';

/* ─── Admin gating — mirrors Skills.jsx/Dashboard.jsx exactly, so a
   skill hidden from the general public there is hidden here too. ─── */
const ADMIN_EMAIL = 'plethora1000@gmail.com';
const isAdmin = (email) => email?.toLowerCase() === ADMIN_EMAIL;
const ADMIN_ONLY_SKILL_IDS = new Set(['copywriting', 'sales-negotiation', 'precision-storytelling']);

/* The `skills` table has no emoji/icon column — this is purely
   decorative, not business data, so a small local map plus a generic
   fallback for any future skill is fine here. */
const SKILL_EMOJI = {
  'ai-prompting': '🤖',
  'sales-negotiation': '🤝',
  'copywriting': '✍️',
  'precision-storytelling': '📖',
  'technical-translation': '🌐',
};

// Maps a Supabase skills row onto the shape this page's screens render.
const toPickerSkill = (s) => ({
  id: s.id,
  title: s.name,
  tagline: s.tagline,
  emoji: SKILL_EMOJI[s.id] || '✨',
  status: s.status,
  accent: s.color_primary || '#ea580c',
  bg: s.color_primary ? `${s.color_primary}14` : '#fff7ed',
  border: s.color_primary ? `${s.color_primary}40` : '#fed7aa',
  href: s.route || s.landing_route || null,
});

/* ─── Step indicator ─────────────────────────────────────── */
const StepDots = ({ step, total }) => (
  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 32 }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{
        width: i === step ? 20 : 6,
        height: 6,
        borderRadius: 99,
        background: i === step ? '#ea580c' : '#e8e4df',
        transition: 'all 0.3s ease',
      }} />
    ))}
  </div>
);

/* ─── Screen 1: Welcome ──────────────────────────────────── */
const WelcomeScreen = ({ profile, onNext }) => {
  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  return (
    <div style={{ textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🏰</div>
      <h1 style={{ fontSize: '2rem', fontWeight: 900, color: '#1c1917', letterSpacing: '-0.04em', marginBottom: 12, lineHeight: 1.1 }}>
        Welcome, {firstName}!
      </h1>
      <p style={{ fontSize: '1.05rem', color: '#78716c', lineHeight: 1.7, marginBottom: 12 }}>
        Castle Atlas is where you build skills that actually matter — through real exercises, not videos.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '28px auto', maxWidth: 340, textAlign: 'left' }}>
        {[
          { icon: '🎯', text: 'Hands-on exercises with instant AI scoring' },
          { icon: '📈', text: 'Track your progress across every skill' },
          { icon: '🔓', text: 'Start free — no credit card ever' },
        ].map(({ icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fff7ed', borderRadius: 12, border: '1px solid #fed7aa' }}>
            <span style={{ fontSize: 20 }}>{icon}</span>
            <span style={{ fontSize: 14, color: '#44403c', fontWeight: 500 }}>{text}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#ea580c', color: '#fff', fontWeight: 700, fontSize: '1rem', padding: '13px 32px', borderRadius: 12, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px -4px rgba(234,88,12,0.45)' }}
      >
        Let's go <ArrowRight size={17} />
      </button>
    </div>
  );
};

/* ─── Screen 2: Skill Picker ─────────────────────────────── */
const SkillPickerScreen = ({ skills, onNext, userEmail }) => {
  const [selected, setSelected]   = useState(null);
  const [notified, setNotified]   = useState({}); // skillId -> true
  const [loading, setLoading]     = useState({});

  const handleNotify = async (e, skillId) => {
    e.stopPropagation();
    if (notified[skillId] || loading[skillId]) return;
    setLoading(prev => ({ ...prev, [skillId]: true }));
    try {
      await supabase.from('waitlist').upsert({
        email: userEmail,
        source: `onboarding_${skillId}`,
        skill_id: skillId,
      }, { onConflict: 'email,skill_id' });
      setNotified(prev => ({ ...prev, [skillId]: true }));
    } catch {}
    setLoading(prev => ({ ...prev, [skillId]: false }));
  };

  const handleContinue = () => {
    if (!selected) return;
    onNext(selected);
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{ fontSize: '1.7rem', fontWeight: 900, color: '#1c1917', letterSpacing: '-0.03em', marginBottom: 8 }}>
          Which skill do you want first?
        </h2>
        <p style={{ fontSize: '0.95rem', color: '#78716c' }}>
          Pick your starting track. For coming-soon skills, tap the bell to get notified.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 28 }}>
        {skills.map(skill => {
          const isLive     = skill.status === 'live';
          const isSelected = selected === skill.id;
          const isNotified = notified[skill.id];
          const isLoading  = loading[skill.id];

          return (
            <div
              key={skill.id}
              onClick={() => setSelected(skill.id)}
              style={{
                background: isSelected ? skill.bg : '#fff',
                border: `2px solid ${isSelected ? skill.accent : '#e8e4df'}`,
                borderRadius: 16,
                padding: '16px 18px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              {/* Emoji */}
              <div style={{ fontSize: 28, flexShrink: 0 }}>{skill.emoji}</div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1917' }}>{skill.title}</span>
                  {isLive
                    ? <span style={{ fontSize: 10, fontWeight: 700, color: '#22c55e', background: '#f0fdf4', padding: '1px 7px', borderRadius: 99, border: '1px solid #bbf7d0' }}>LIVE</span>
                    : <Lock size={11} color="#a8a29e" />
                  }
                </div>
                <p style={{ fontSize: 12, color: '#78716c', lineHeight: 1.4, margin: 0 }}>{skill.tagline}</p>
              </div>

              {/* Right side */}
              <div style={{ flexShrink: 0 }}>
                {isSelected && isLive && (
                  <CheckCircle size={20} color={skill.accent} />
                )}
                {isSelected && !isLive && (
                  <button
                    onClick={e => handleNotify(e, skill.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 8, border: 'none',
                      background: isNotified ? '#f0fdf4' : skill.bg,
                      color: isNotified ? '#22c55e' : skill.accent,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {isLoading
                      ? <div style={{ width: 12, height: 12, border: `2px solid ${skill.accent}40`, borderTopColor: skill.accent, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                      : isNotified
                        ? <><CheckCircle size={12} /> Done!</>
                        : <><Bell size={12} /> Notify me</>
                    }
                  </button>
                )}
                {!isSelected && !isLive && (
                  <Bell size={16} color="#d4cdc7" />
                )}
                {!isSelected && isLive && (
                  <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #e8e4df' }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center' }}>
        <button
          onClick={handleContinue}
          disabled={!selected}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: selected ? '#ea580c' : '#e8e4df',
            color: selected ? '#fff' : '#a8a29e',
            fontWeight: 700, fontSize: '1rem',
            padding: '13px 32px', borderRadius: 12,
            border: 'none', cursor: selected ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            boxShadow: selected ? '0 4px 20px -4px rgba(234,88,12,0.45)' : 'none',
          }}
        >
          Continue <ArrowRight size={17} />
        </button>
      </div>
    </div>
  );
};

/* ─── Screen 3: Launch ───────────────────────────────────── */
const LaunchScreen = ({ skills, selectedSkill, onComplete }) => {
  const skill = skills.find(s => s.id === selectedSkill);
  const isLive = skill?.status === 'live';

  return (
    <div style={{ textAlign: 'center', maxWidth: 440, margin: '0 auto' }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>{skill?.emoji || '🚀'}</div>
      <h2 style={{ fontSize: '1.8rem', fontWeight: 900, color: '#1c1917', letterSpacing: '-0.03em', marginBottom: 12, lineHeight: 1.1 }}>
        {isLive ? "You're all set!" : "You're on the list!"}
      </h2>
      <p style={{ fontSize: '1rem', color: '#78716c', lineHeight: 1.7, marginBottom: 28 }}>
        {isLive
          ? <>Your <strong>{skill.title}</strong> track is ready. Your first lesson is waiting — let's start building.</>
          : <>We'll notify you the moment <strong>{skill.title}</strong> drops. In the meantime, the AI Prompt Engineering course is live and free.</>
        }
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
        <button
          onClick={() => onComplete(isLive ? skill.href : '/lessons')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#ea580c', color: '#fff', fontWeight: 700, fontSize: '1rem', padding: '13px 32px', borderRadius: 12, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px -4px rgba(234,88,12,0.45)', width: '100%', justifyContent: 'center' }}
        >
          {isLive ? 'Start my first lesson' : 'Try AI Prompting now'} <ArrowRight size={17} />
        </button>
        {!isLive && (
          <button
            onClick={() => onComplete('/skills')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', color: '#78716c', fontWeight: 600, fontSize: '0.9rem', padding: '10px 24px', borderRadius: 12, border: '1.5px solid #e8e4df', cursor: 'pointer', width: '100%', justifyContent: 'center' }}
          >
            Browse all skills
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── Main Onboarding ────────────────────────────────────── */
const Onboarding = () => {
  const { user, profile, updateProfile } = useAuth();
  const { skills: rawSkills, loading: skillsLoading } = useSkills();
  const navigate = useNavigate();
  const [step, setStep]                   = useState(0);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [completing, setCompleting]       = useState(false);
  const [verifying, setVerifying]         = useState(true);

  const isAdminUser = isAdmin(user?.email);
  const skills = rawSkills
    .filter((s) => (s.status === 'live' || s.status === 'coming-soon') && (!ADMIN_ONLY_SKILL_IDS.has(s.id) || isAdminUser))
    .map(toPickerSkill);

  // Handle email confirmation token from URL
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type      = params.get('type');

    if (tokenHash && type) {
      supabase.auth.verifyOtp({ token_hash: tokenHash, type })
        .then(async ({ data, error }) => {
          if (error) {
            console.error('Token verify error:', error.message);
          } else {
            // Force session refresh so AuthContext picks up the new user
            await supabase.auth.refreshSession();
          }
          // Clean URL so token isn't reused
          window.history.replaceState({}, '', '/onboarding');
          setVerifying(false);
        });
    } else {
      setVerifying(false);
    }
  }, []);

  const handleSkillPicked = (skillId) => {
    setSelectedSkill(skillId);
    setStep(2);
  };

  const handleComplete = async (destination) => {
    if (completing) return;
    setCompleting(true);
    try {
      await updateProfile({
        onboarding_completed: true,
        onboarding_skill: selectedSkill,
      });
    } catch (e) {
      console.warn('Profile update failed:', e?.message);
    }
    navigate(destination || '/lessons', { replace: true });
  };

  // Show spinner while verifying token or while the skill picker's data
  // (Supabase skills table) is still loading
  if (verifying || skillsLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#faf9f7' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 24, height: 24, border: '3px solid #fed7aa', borderTopColor: '#ea580c', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", minHeight: '100vh', background: '#faf9f7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 36 }}>
        <img src="/logo.svg" alt="Castle Atlas" style={{ height: 28 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1917' }}>Castle Atlas</span>
      </div>

      <StepDots step={step} total={3} />

      <div style={{ width: '100%', animation: 'fadeUp 0.35s ease' }} key={step}>
        {step === 0 && <WelcomeScreen profile={profile} onNext={() => setStep(1)} />}
        {step === 1 && <SkillPickerScreen skills={skills} onNext={handleSkillPicked} userEmail={user?.email} />}
        {step === 2 && <LaunchScreen skills={skills} selectedSkill={selectedSkill} onComplete={handleComplete} />}
      </div>
    </div>
  );
};

export default Onboarding;